import { OrderBy, useReviewsQuery } from 'generated/graphql';
import { useProReviewsCount } from 'hooks/use-pro-reviews-count/use-pro-reviews-count';
import { useCallback, useEffect, useRef } from 'react';
import { useReviewFilters } from './useReviewFilters';

type UseReviewListParams = {
  currentBilikZoneIds?: number[];
};

export const useReviewList = ({ currentBilikZoneIds }: UseReviewListParams) => {
  const { filters, setFilters, reviewsPerPage, whereTable, whereTodo } =
    useReviewFilters({
      currentBilikZoneIds,
    });

  const { count, refetch: refetchCount } = useProReviewsCount(whereTable);
  const { count: countTodo, refetch: refetchTodoCount } =
    useProReviewsCount(whereTodo);

  const orderByColumn = filters.orderBy?.column ?? 'dateCreated';
  const direction =
    filters.orderBy?.direction === 'ascending'
      ? OrderBy.Asc
      : OrderBy.DescNullsLast;
  const offset = filters.page ? (filters.page - 1) * reviewsPerPage : 0;

  const {
    data,
    loading,
    refetch: refetchReviews,
  } = useReviewsQuery({
    variables: {
      limit: reviewsPerPage,
      offset,
      order_by: [
        {
          [orderByColumn]: direction,
        },
      ],
      where: whereTable,
    },
  });

  const reviews = data?.proReview;

  // Keep the position of the selected review: when it leaves the list (e.g.
  // moderated out of the filtered view), the following review takes its place,
  // falling back to the last one if it was at the end.
  const selectedIndexRef = useRef(0);
  const selectedIndex =
    reviews?.findIndex((review) => review.id === filters.selectedId) ?? -1;
  useEffect(() => {
    if (selectedIndex !== -1) {
      selectedIndexRef.current = selectedIndex;
    }
  }, [selectedIndex]);
  const selectedReview = reviews?.length
    ? reviews[
        selectedIndex !== -1
          ? selectedIndex
          : Math.min(selectedIndexRef.current, reviews.length - 1)
      ]
    : undefined;

  const refetch = useCallback(async (): Promise<void> => {
    await Promise.all([refetchReviews(), refetchCount(), refetchTodoCount()]);
  }, [refetchReviews, refetchCount, refetchTodoCount]);

  return {
    reviews,
    selectedReview,
    loading,
    count,
    countTodo,
    refetch,
    filters,
    setFilters,
  };
};
